/**
 * 原因码 → 用户可读的客观解释文案（§23：客观、简洁、无羞辱感）。
 *
 * 展示给用户的措辞遵守规格要求：
 *   - 用「本次维持重量」「本次减少一组」「当前数据不足，暂不调整」等中性表述；
 *   - 不使用「你失败了」「吃多了会胖」等表述。
 */

import type { ProgressionReasonCode } from "@jilian/shared";

const REASON_TEXT: Record<ProgressionReasonCode, string> = {
  ALL_SETS_AT_UPPER_BOUND: "所有工作组都已达到次数区间上限。",
  RIR_COMPLIANT: "各组的余力（RIR）都符合目标。",
  LARGE_INCREMENT_REQUIRES_TRIAL: "下一个重量档位增幅超过 10%，先试重并稳定次数。",
  TWO_CONSECUTIVE_COMPARABLE_FAILURES: "连续两次正常训练未达到最低次数，建议下次降低一个最小档位。",
  FIRST_FAILURE_MAINTAIN: "第一次未达到最低次数，本次维持重量。",
  CONTINUE_DOUBLE_PROGRESSION: "继续双进阶：先在同一重量内增加次数，达标后再加重。",
  INCOMPLETE_DATA: "本次数据不完整（组数/次数/余力缺失或提前结束），暂不调整重量。",
  PAIN_REPORTED: "本次记录了伤处不适，撤销加重建议并重新评估。",
  FORM_BREAKDOWN: "本次有动作失控/变形记录，维持重量。",
  REDUCED_DAY_NO_PROGRESSION: "今天是降量/减量训练，不执行自动加重。",
  UNPLANNED_FAILURE: "超过目标强度（RIR 0）：保存真实记录，本次不自动加重，下次请保留至少 1 次余力。",
  SHORT_REST: "实际休息明显短于建议时间，本次维持重量。",
  LIGHT_DAY_SKIP_COUNT: "轻训练日不计入进阶与失败统计。",
  USER_OVERRIDE: "你手动接受了这次调整（原建议已保存）。",
  DEVICE_DIFFERENCE: "器械或环境与上次不同，本次维持重量。",
  NEW_MACHINE_CALIBRATION: "新动作首次完成，先校准实际工作重量。",
  JOINT_PAIN: "本次记录了关节疼痛，退出普通进阶并进入动作替换评估。",
  FOOT_SLIGHTLY_WORSE: "右脚状态略差，暂停今天自动加重。",
  FOOT_SIGNIFICANTLY_WORSE: "右脚状态明显变差，暂停正常训练并进入保守模板（需再次确认）。",
  // 第五训练日 / 减量周（§13 / 决策 14.12）
  WEEK_FORMAL_TRAININGS_BELOW_3: "本周正式训练不足 3 次，轻训练日暂不开放。",
  TOO_SOON_SINCE_LAST_FORMAL_TRAINING: "距上次正式训练不足 18 小时，轻训练日暂不开放。",
  RECENT_DECLINING_EXERCISES: "近期有多个动作连续表现下降，暂不开放轻训练日。",
  READINESS_NOT_NORMAL: "当前恢复状态不满足条件，轻训练日暂不开放。",
  INJURY_CONSTRAINTS_NOT_MET: "当前伤情条件不满足，轻训练日暂不开放。",
  DELOAD_WEEK_6_EVALUATION: "连续训练已达 6 周，建议评估是否需要减量周。",
  DELOAD_WEEK_8_REMINDER: "连续训练已达 8 周，建议安排减量周。",
  DELOAD_FATIGUE_SIGNALS: "疲劳信号累积较多（表现下降/睡眠/恢复等），建议减量。",
};

const UNKNOWN: ProgressionReasonCode = "CONTINUE_DOUBLE_PROGRESSION";

/** 单条原因码 → 文案（未知码回退为通用文案，绝不抛错）。 */
export function reasonText(code: ProgressionReasonCode): string {
  return REASON_TEXT[code] ?? REASON_TEXT[UNKNOWN]!;
}

/** 一组原因码 → 拼接文案（去重、按传入顺序）。 */
export function reasonTextList(codes: readonly ProgressionReasonCode[]): string {
  const seen = new Set<string>();
  return codes
    .filter((c) => (seen.has(c) ? false : (seen.add(c), true)))
    .map(reasonText)
    .join(" ");
}